/**
 * Field · Input · Select — form kontrolleri.
 *
 * ─── ETİKET, HATA VE YARDIM METNİ BİLEŞENİN İÇİNDE ───────────────────────────
 * `Field` etiketi girdiye `htmlFor`/`id` ile BAĞLAR ve hata metnini
 * `aria-describedby` ile ilişkilendirir. Her formda elle yazılsaydı bir
 * kısmında unutulur, ekran okuyucu kullanıcısı hatayı hiç duymazdı.
 *
 * Hata durumu ayrıca `aria-invalid` ile işaretlenir — renk TEK BAŞINA anlam
 * taşımaz (styles.css'teki not).
 */
import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';

import { cx } from './cx.ts';

const CONTROL =
  'w-full min-h-11 rounded-[var(--radius-control)] border bg-surface px-3 text-base ' +
  'placeholder:text-ink-muted disabled:opacity-50 disabled:cursor-not-allowed';

export interface FieldProps {
  readonly label: string;
  /*
   * `| undefined` AÇIKÇA yazılıyor: `exactOptionalPropertyTypes` açık olduğu
   * için "alan yok" ile "alan var ama undefined" farklı şeyler. Form
   * kütüphaneleri hata alanını `undefined` olarak GEÇER (hata yokken), bu
   * yüzden ikisini de kabul etmeliyiz.
   */
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly required?: boolean | undefined;
  readonly children: (props: {
    id: string;
    /** Girdiye geçirilir — zorunluluk BURADAN bildirilir, etiketten değil. */
    required: boolean;
    /* `| undefined` ŞART: exactOptionalPropertyTypes açık — yardım metni
       yoksa alan hiç verilmemeli değil, açıkça `undefined` olmalı. */
    describedBy: string | undefined;
    invalid: boolean;
  }) => ReactNode;
}

export function Field({ label, hint, error, required, children }: FieldProps): React.JSX.Element {
  const id = useId();
  const helpId = `${id}-help`;
  const invalid = error !== undefined && error !== '';

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required === true && (
          /*
           * Yıldız GÖRSELDİR, etiketin bir parçası değil.
           *
           * `aria-label="zorunlu"` verilseydi etiketin erişilebilir adı
           * "E-posta zorunlu" olurdu — ekran okuyucu alanın adını yanlış
           * okur, ve isme göre arayan her şey (testler dâhil) bulamaz.
           * Zorunluluk, girdinin KENDİ `required` niteliğiyle bildirilir;
           * ekran okuyucular bunu zaten doğru anons eder.
           */
          <span className="ml-0.5 text-danger" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children({
        id,
        required: required === true,
        describedBy: hint !== undefined || invalid ? helpId : undefined,
        invalid,
      })}
      {(hint !== undefined || invalid) && (
        <p
          id={helpId}
          className={cx('text-sm', invalid ? 'text-danger' : 'text-ink-muted')}
          // Hata SONRADAN belirdiği için duyurulmalı.
          role={invalid ? 'alert' : undefined}
        >
          {invalid ? error : hint}
        </p>
      )}
    </div>
  );
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  readonly invalid?: boolean;
}

export function Input({ invalid = false, ...rest }: InputProps): React.JSX.Element {
  return (
    <input
      aria-invalid={invalid || undefined}
      className={cx(CONTROL, invalid ? 'border-danger' : 'border-border')}
      {...rest}
    />
  );
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  readonly invalid?: boolean;
}

/**
 * Yerel `<select>` KASITLI.
 *
 * Radix'in özel açılır listesi masaüstünde güzeldir ama mobilde işletim
 * sisteminin kendi seçicisi (iOS tekerleği, Android listesi) hem daha hızlı
 * hem daha tanıdıktır. Mobil öncelikli bir üründe yerel olanı yeniden
 * yazmak kullanıcıya kaybettirir.
 */
export function Select({ invalid = false, children, ...rest }: SelectProps): React.JSX.Element {
  return (
    <select
      aria-invalid={invalid || undefined}
      className={cx(CONTROL, 'appearance-none pr-9', invalid ? 'border-danger' : 'border-border')}
      {...rest}
    >
      {children}
    </select>
  );
}
